export type FeatureFlag =
  // Tag System
  | 'expression_tags'
  | 'tag_udts'
  | 'deadbanding'
  // Production
  | 'oee'
  | 'downtime_tracking'
  | 'quality_tracking'
  | 'shift_scheduling'
  | 'andon'
  | 'labor_efficiency'
  // Alarms & Reporting
  | 'alarm_pipelines'
  | 'sms_voice_notifications'
  | 'reporting'
  | 'isa_alarm_metrics'
  | 'trend_alarms'
  | 'change_trace'
  // Data Infrastructure
  | 'store_forward'
  | 'transaction_groups'
  | 'rest_api_outbound'
  | 'event_streams'
  // MES
  | 'work_orders'
  | 'recipe_management'
  | 'work_instructions'
  // Visualization
  | 'spc_enhanced'
  | 'symbol_factory'
  | 'dashboard_templates'
  // Control
  | 'ladder_logic'
  | 'sfc_logic'
  | 'python_scripting'
  | 'acme_certificates'
  // Connectivity
  | 'mqtt_sparkplug'
  | 'remote_databases'
  | 'data_connectors'
  | 'mesh_networking'
  | 'redundancy'
  // Machine vision
  | 'machine_vision'
  // Audit
  | 'enhanced_audit'
  // Core Page Access (always enabled system-wide, restricted per-user)
  | 'page_devices'
  | 'page_tags'
  | 'page_local_tags'
  | 'page_scanner'
  | 'page_smart_assist'
  | 'page_dashboards'
  | 'page_scripts'
  | 'page_libraries'
  | 'page_custom_pages'
  | 'page_data_tables'
  | 'page_workflows'
  | 'page_spc'
  | 'page_history'
  | 'page_settings'
  | 'page_backup'
  | 'page_audit_log';

export type LicenseTier = 'community' | 'professional' | 'enterprise';

/**
 * Features included in each license tier.
 * Tiers are cumulative: professional includes community, enterprise includes all.
 */
export const TIER_FEATURES: Record<LicenseTier, FeatureFlag[]> = {
  community: [
    'expression_tags',
    'deadbanding',
    'enhanced_audit',
    'spc_enhanced',
    'shift_scheduling',
  ],
  professional: [
    'oee',
    'downtime_tracking',
    'quality_tracking',
    'andon',
    'alarm_pipelines',
    'sms_voice_notifications',
    'reporting',
    'isa_alarm_metrics',
    'store_forward',
    'transaction_groups',
    'tag_udts',
    // Paired with tag_udts on purpose: templates are what UDTs are FOR, and a
    // tier that gives you the type system without the screens that consume it
    // would be selling half a mechanism.
    'dashboard_templates',
    'rest_api_outbound',
    'ladder_logic',
    'remote_databases',
  ],
  enterprise: [
    'work_orders',
    'mqtt_sparkplug',
    'work_instructions',
    'recipe_management',
    'symbol_factory',
    'labor_efficiency',
    'sfc_logic',
  ],
};

/** Get all features available for a given tier (cumulative). */
export function getFeaturesForTier(tier: LicenseTier): FeatureFlag[] {
  const tiers: LicenseTier[] = ['community', 'professional', 'enterprise'];
  const tierIndex = tiers.indexOf(tier);
  const features: FeatureFlag[] = [];
  for (let i = 0; i <= tierIndex; i++) {
    features.push(...TIER_FEATURES[tiers[i]]);
  }
  return features;
}

export type FeatureCategory =
  | 'pages'
  | 'tag_system'
  | 'production'
  | 'alarms_reporting'
  | 'data'
  | 'mes'
  | 'visualization'
  | 'control'
  | 'connectivity'
  | 'vision'
  | 'audit';

export interface FeatureInfo {
  name: string;
  description: string;
  category: FeatureCategory;
}

export const FEATURE_METADATA: Record<FeatureFlag, FeatureInfo> = {
  expression_tags: {
    name: 'Expression Tags',
    description: 'Virtual tags computed from formulas referencing other tags',
    category: 'tag_system',
  },
  tag_udts: {
    name: 'Tag UDTs',
    description: 'User-defined tag templates for standardizing device models',
    category: 'tag_system',
  },
  deadbanding: {
    name: 'Deadbanding',
    description: 'Configurable change thresholds to reduce data noise and storage',
    category: 'tag_system',
  },
  oee: {
    name: 'OEE Tracking',
    description: 'Overall Equipment Effectiveness with Availability, Performance, and Quality',
    category: 'production',
  },
  downtime_tracking: {
    name: 'Downtime Tracking',
    description: 'Downtime reason codes, event logging, and Pareto analysis',
    category: 'production',
  },
  quality_tracking: {
    name: 'Quality Tracking',
    description: 'Reject/scrap reason codes and quality metrics',
    category: 'production',
  },
  shift_scheduling: {
    name: 'Shift Scheduling',
    description: 'Shift definitions with takt targets, breaks, and production calendar',
    category: 'production',
  },
  andon: {
    name: 'Andon Display',
    description: 'Shop floor scoreboard showing machine status, OEE, and targets',
    category: 'production',
  },
  labor_efficiency: {
    name: 'Labor Efficiency',
    description: 'Track earned labor time vs target per operator and work order',
    category: 'production',
  },
  alarm_pipelines: {
    name: 'Alarm Pipelines',
    description: 'Notification escalation with email/SMS, delays, and notification groups',
    category: 'alarms_reporting',
  },
  sms_voice_notifications: {
    name: 'SMS & Voice Alerts',
    description: 'Send alarm notifications as SMS texts and voice calls via the Scada.Ink cloud relay (metered subscription)',
    category: 'alarms_reporting',
  },
  trend_alarms: {
    name: 'Trend Alarms',
    description: 'Alarm rules that judge a tag against its own history — a trend projected to cross a limit, a drift from normal, or a tag changing slower or faster than usual',
    category: 'alarms_reporting',
  },
  change_trace: {
    name: 'Change Trace',
    description: 'Pick a time window (or the last N parts of a counter) and see which tags changed or behaved differently from normal, with the operator writes behind them',
    category: 'data',
  },
  isa_alarm_metrics: {
    name: 'ISA 18.2 Alarm Metrics',
    description: 'Alarm-system performance against ISA 18.2 / EEMUA 191 — rate, floods, chattering, bad actors, stale alarms',
    category: 'alarms_reporting',
  },
  reporting: {
    name: 'Reporting Module',
    description: 'Drag-and-drop report designer; generate PDF or HTML reports on demand or emailed on a schedule',
    category: 'alarms_reporting',
  },
  store_forward: {
    name: 'Store & Forward',
    description: 'Buffer data locally during database outages, auto-forward on recovery',
    category: 'data',
  },
  transaction_groups: {
    name: 'Transaction Groups',
    description: 'Scheduled tag-value snapshots logged to database tables',
    category: 'data',
  },
  rest_api_outbound: {
    name: 'REST API Outbound',
    description: 'Push data to external REST endpoints on triggers',
    category: 'data',
  },
  event_streams: {
    name: 'Event Streams',
    description:
      'Pipelines from tag changes, alarms, messages, HTTP, MQTT or a timer, through filter and transform stages, '
      + 'to tags, tables, HTTP, MQTT, Kafka, workflows and messages, with retries and a dead-letter list',
    category: 'data',
  },
  work_orders: {
    name: 'Work Orders',
    description: 'Job/work order tracking with expected vs actual production counts',
    category: 'mes',
  },
  recipe_management: {
    name: 'Recipe Management',
    description: 'Configurable recipe parameters per product with changeover management',
    category: 'mes',
  },
  work_instructions: {
    name: 'Work Instructions',
    description: 'Step-by-step operator guidance with multimedia and sign-off tracking',
    category: 'mes',
  },
  spc_enhanced: {
    name: 'Enhanced SPC',
    description: 'Western Electric/Nelson rules, Cp/Cpk trends, and advanced control charts',
    category: 'visualization',
  },
  dashboard_templates: {
    name: 'Dashboard Templates',
    description:
      'Build one faceplate and place it on every machine. Parameters bind it to a UDT instance, '
      + 'and a repeater renders it once per instance — so fifty pumps are one screen and one fix.',
    category: 'visualization',
  },
  symbol_factory: {
    name: 'Symbol Factory',
    description: 'Library of industrial SVG graphics for dashboards and HMI',
    category: 'visualization',
  },
  ladder_logic: {
    name: 'Ladder Logic',
    description: 'Soft-PLC with CompactLogix-style ladder diagram editor and continuous scan engine',
    category: 'control',
  },
  sfc_logic: {
    name: 'Sequential Function Charts',
    description: 'IEC 61131-3 SFC routines — step/transition sequencing with actions, running on the ladder scan engine',
    category: 'control',
  },
  python_scripting: {
    name: 'Python Scripting',
    description: 'Write scripts and script libraries in Python as well as JavaScript, with the same triggers and API, '
      + 'numpy, pandas, SciPy, statsmodels and scikit-learn built in, and admin-installed packages. Runs in the same sandbox as JavaScript',
    category: 'control',
  },
  acme_certificates: {
    name: 'ACME certificates (Let\u2019s Encrypt)',
    description:
      'Automatic certificates from a public CA over DNS-01. Requires outbound internet and a real domain name, '
      + 'so it is off by default — an air-gapped appliance has neither and should use its own CA instead.',
    category: 'control',
  },
  mqtt_sparkplug: {
    name: 'MQTT Sparkplug B',
    description: 'Sparkplug B protocol with birth/death certificates and auto-discovery',
    category: 'connectivity',
  },
  remote_databases: {
    name: 'Remote Databases',
    description: 'Connect to external databases (SQL Server, MySQL, PostgreSQL, Oracle, ODBC, MongoDB) for data exchange and store-forward',
    category: 'connectivity',
  },
  data_connectors: {
    name: 'Data Connectors',
    description: 'Publish tag changes, alarms and batch records to Apache Kafka, AWS IoT Core, Azure IoT Hub, Google Cloud Pub/Sub or MongoDB, '
      + 'with store & forward. Needs a network path to the target, so it is off until an admin turns it on.',
    category: 'connectivity',
  },
  mesh_networking: {
    name: 'Mesh Networking',
    description: 'Pair Scada.Ink appliances over any network so they read and write each other\'s shared tags, see and acknowledge each other\'s alarms, catch up on history after an outage, and send each other script and workflow messages',
    category: 'connectivity',
  },
  redundancy: {
    name: 'Redundancy',
    description: 'Run two appliances as a primary and backup pair: the backup keeps a copy of the configuration and history, '
      + 'takes over device polling and the shared address when the primary fails, and hands back when asked',
    category: 'connectivity',
  },
  machine_vision: {
    name: 'Machine Vision',
    description:
      'Cameras as devices: inspection tools (count, presence, color, pattern, caliper, gauges, OCV, barcodes), '
      + 'pass/fail tags, failed-part image archive, and millimeter and robot coordinates',
    category: 'vision',
  },
  enhanced_audit: {
    name: 'Enhanced Audit Trail',
    description: 'Record old and new values of changed fields for shifts, work instructions, downtime and quality reasons, recipes and work orders',
    category: 'audit',
  },
  // Core page access flags (always enabled system-wide, restricted per-user only)
  page_devices: { name: 'Devices', description: 'Device configuration and monitoring', category: 'pages' },
  page_tags: { name: 'Tags', description: 'Tag browser and configuration', category: 'pages' },
  page_local_tags: { name: 'Local Tags', description: 'Local memory tags', category: 'pages' },
  page_scanner: { name: 'Scanner', description: 'Network device discovery', category: 'pages' },
  page_smart_assist: { name: 'Smart Assist', description: 'AI-powered configuration assistant', category: 'pages' },
  page_dashboards: { name: 'Dashboards', description: 'Dashboard builder and viewer', category: 'pages' },
  page_scripts: { name: 'Scripts', description: 'Server-side script editor', category: 'pages' },
  page_libraries: { name: 'Libraries', description: 'Reusable script libraries', category: 'pages' },
  page_custom_pages: { name: 'Custom Pages', description: 'Custom HTML/CSS/JS pages', category: 'pages' },
  page_data_tables: { name: 'Data Tables', description: 'Structured data storage tables', category: 'pages' },
  page_workflows: { name: 'Workflows', description: 'Visual node-based automation', category: 'pages' },
  page_spc: { name: 'SPC', description: 'Statistical process control charts', category: 'pages' },
  page_history: { name: 'History', description: 'Tag history browser', category: 'pages' },
  page_settings: { name: 'Settings', description: 'System configuration', category: 'pages' },
  page_backup: { name: 'Backup', description: 'Backup and restore', category: 'pages' },
  page_audit_log: { name: 'Audit Log', description: 'System audit trail', category: 'pages' },
};

export const FEATURE_CATEGORY_LABELS: Record<FeatureCategory, string> = {
  pages: 'Page Access',
  tag_system: 'Tag System',
  production: 'Production',
  alarms_reporting: 'Alarms & Reporting',
  data: 'Data Infrastructure',
  mes: 'Manufacturing Execution',
  visualization: 'Visualization',
  control: 'Control & Logic',
  connectivity: 'Connectivity',
  vision: 'Machine Vision',
  audit: 'Audit & Compliance',
};

/**
 * Drivers that need a licensed feature before a device can use them. The API
 * refuses to start such a device without it and the Add Device wizard shows it
 * locked, so both read this one map. Every other driver is part of the core.
 */
export const DRIVER_REQUIRED_FEATURE: Partial<Record<string, FeatureFlag>> = {
  vision: 'machine_vision',
};

/** All feature flags as an array. */
export const ALL_FEATURE_FLAGS: FeatureFlag[] = Object.keys(FEATURE_METADATA) as FeatureFlag[];

/** Core page feature flags — always enabled system-wide, only restricted per-user. */
export const CORE_FEATURE_FLAGS: FeatureFlag[] = ALL_FEATURE_FLAGS.filter(
  (f) => FEATURE_METADATA[f].category === 'pages'
);

/** Why a feature is licensed: free for everyone, the perpetual key, or only a live trial. */
export type FeatureLicenseSource = 'base' | 'license' | 'trial';

/** One trial key stored on the appliance (alongside the perpetual key). */
export interface FeatureTrial {
  /** Cloud licence id (`lid`) of the trial key. */
  licenseId: string;
  /** Licensed features in the key that this build knows, base set excluded. */
  features: FeatureFlag[];
  /** ISO expiry. */
  expires: string;
  /** Expired trials stay listed until removed but grant nothing. */
  expired: boolean;
  /** Whole days left, rounded up; 0 once expired. */
  daysRemaining: number;
  /** Set when the stored key no longer verifies on this appliance; it then grants nothing. */
  problem?: string;
}

export interface FeatureState {
  /** Derived from the whole licensed set: perpetual key plus live trials. */
  tier: LicenseTier;
  /**
   * `customer`, `expires`, `isPermanent`, `isExpired`, `daysRemaining` and
   * `licenseId` describe the perpetual key only; trials are in `trials`.
   */
  customer: string;
  expires: string;
  isPermanent: boolean;
  isExpired: boolean;
  daysRemaining: number;
  features: Record<FeatureFlag, {
    licensed: boolean;
    enabled: boolean;
    /** Set when licensed. `trial` = only a live trial grants it (it stops when the trial ends). */
    source?: FeatureLicenseSource;
    /** When source is `trial`: the latest expiry among the live trials that grant it (ISO). */
    trialExpires?: string;
  }>;
  /** Trial keys stored on this appliance, newest first. Empty when none. */
  trials: FeatureTrial[];
  /** Normalised hardware ID licence keys are locked to; null when it cannot be read. */
  hardwareId: string | null;
  /** Cloud licence id (`lid`) of the verified key; null without one. */
  licenseId: string | null;
  /** Why the stored (or missing) licence grants nothing beyond Community; null when fine. */
  licenseProblem: string | null;
  /**
   * Product edition the verified perpetual key was issued for (absent in the
   * key = scada); null without one. `tier` only says whether a whole tier's
   * features are licensed, so an à la carte Scada.Ink or an HMI.ink licence
   * reads Community there — show this alongside it.
   */
  edition?: 'scada' | 'hmi' | null;
}
